/**
 * voiceOutbox — IndexedDB-backed outbox for AI Chat Buddy voice uploads.
 *
 * Why this exists: a learner who records a voice turn while on flaky cellular
 * (commute, basement, elevator, Wi-Fi handoff) used to lose the entire
 * recording the moment the upload hit a network error. Re-recording is
 * disproportionately painful for voice — the user has to re-summon the
 * sentence in their head — so we persist the encoded WAV blob and let a
 * background scheduler retry on exponential backoff. Recordings survive
 * navigation and tab restart.
 *
 * Schema:
 *   DB:    loopy-voice-outbox (version 1)
 *   Store: pending  (keyPath: 'tempId')
 *     index: by_nextAttemptAt
 *
 * The store holds at most a few records at a time (mobile users on flaky
 * networks). We deliberately keep the API small — the scheduler hook reads
 * all records and decides what to do, instead of pushing scheduling logic
 * into IDB.
 */

const DB_NAME = 'loopy-voice-outbox';
const DB_VERSION = 1;
const STORE = 'pending';

export interface VoiceOutboxRecord {
    tempId: string;
    conversationId: string;
    wavBlob: Blob;
    sampleRate: number;
    createdAt: number;
    attempts: number;
    /** Epoch ms — scheduler skips records whose nextAttemptAt is in the future. */
    nextAttemptAt: number;
    /** Last upload-error message, surfaced in the bubble. */
    lastError: string | null;
    /** Set true once attempts >= MAX_ATTEMPTS — scheduler stops retrying. */
    dead: boolean;
}

let dbPromise: Promise<IDBDatabase> | null = null;

function openDb(): Promise<IDBDatabase> {
    if (dbPromise) return dbPromise;
    dbPromise = new Promise((resolve, reject) => {
        if (typeof indexedDB === 'undefined') {
            reject(new Error('indexeddb_unavailable'));
            return;
        }
        const req = indexedDB.open(DB_NAME, DB_VERSION);
        req.onupgradeneeded = () => {
            const db = req.result;
            if (!db.objectStoreNames.contains(STORE)) {
                const store = db.createObjectStore(STORE, { keyPath: 'tempId' });
                store.createIndex('by_nextAttemptAt', 'nextAttemptAt');
            }
        };
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error ?? new Error('idb_open_failed'));
    });
    return dbPromise;
}

function withStore<T>(
    mode: IDBTransactionMode,
    fn: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
    return openDb().then(
        (db) =>
            new Promise<T>((resolve, reject) => {
                const tx = db.transaction(STORE, mode);
                const store = tx.objectStore(STORE);
                const req = fn(store);
                req.onsuccess = () => resolve(req.result);
                req.onerror = () => reject(req.error ?? new Error('idb_op_failed'));
            }),
    );
}

export async function enqueueVoiceOutbox(record: Omit<VoiceOutboxRecord, 'attempts' | 'nextAttemptAt' | 'lastError' | 'dead'>): Promise<void> {
    const full: VoiceOutboxRecord = {
        ...record,
        attempts: 0,
        nextAttemptAt: Date.now(),
        lastError: null,
        dead: false,
    };
    await withStore('readwrite', (store) => store.put(full));
}

export async function deleteVoiceOutbox(tempId: string): Promise<void> {
    await withStore('readwrite', (store) => store.delete(tempId));
}

export async function listVoiceOutbox(): Promise<VoiceOutboxRecord[]> {
    return withStore('readonly', (store) => store.getAll() as IDBRequest<VoiceOutboxRecord[]>);
}

export async function getVoiceOutbox(tempId: string): Promise<VoiceOutboxRecord | null> {
    const result = await withStore('readonly', (store) => store.get(tempId) as IDBRequest<VoiceOutboxRecord | undefined>);
    return result ?? null;
}

/**
 * Backoff schedule (in ms) keyed by previous attempts count.
 * After MAX_ATTEMPTS, mark dead and stop retrying — user can manually
 * retry or discard from the bubble.
 */
const BACKOFF_MS = [1_000, 2_000, 4_000, 8_000, 16_000];
export const MAX_ATTEMPTS = BACKOFF_MS.length;

export async function recordAttemptFailure(tempId: string, error: string): Promise<VoiceOutboxRecord | null> {
    const existing = await getVoiceOutbox(tempId);
    if (!existing) return null;
    const newAttempts = existing.attempts + 1;
    const dead = newAttempts >= MAX_ATTEMPTS;
    const delay = BACKOFF_MS[Math.min(existing.attempts, BACKOFF_MS.length - 1)];
    const updated: VoiceOutboxRecord = {
        ...existing,
        attempts: newAttempts,
        nextAttemptAt: Date.now() + delay,
        lastError: error,
        dead,
    };
    await withStore('readwrite', (store) => store.put(updated));
    return updated;
}

/**
 * Manually re-arm a dead record so the scheduler picks it up immediately.
 * Used by the bubble's "retry" button after MAX_ATTEMPTS exhausted.
 */
export async function rearmVoiceOutbox(tempId: string): Promise<void> {
    const existing = await getVoiceOutbox(tempId);
    if (!existing) return;
    const updated: VoiceOutboxRecord = {
        ...existing,
        attempts: 0,
        nextAttemptAt: Date.now(),
        lastError: null,
        dead: false,
    };
    await withStore('readwrite', (store) => store.put(updated));
}
